import { createAction } from '@reduxjs/toolkit'
import { SagaIterator } from 'redux-saga'
import { all, call, put, SagaReturnType, select, takeLatest } from 'redux-saga/effects'

import { setActiveDrawable, setActiveDrawableGroup, UPDATE_OPENING_GROUPS_SUCCESS } from '../../../actions/drawable'
import { deleteOpeningGroupMaterial, deleteOpenings } from '../../../api/projects-api'
import { ActiveFloor } from '../../../models/activeFloor'
import { Project } from '../../../models/project'
import managers from '../../lib/managers'
import PaperManager from '../../lib/managers/PaperManager'
import { Select, Workspace } from '../../lib/toolBoxes/2D'
import {
    deleteDrawableLocation,
    selectCurrentEstimatedOptionOverlayChunkIds,
    selectCurrentOverlayChunkIds,
} from '../../slices/2D'
import { selectDrawableActiveFloor } from '../../slices/documents'
import { ActiveGeometryGroup, GeometryGroup } from '../../slices/geometry'
import {
    isMaterialOpeningInOptions,
    selectAreBaseHomeMaterialsReadOnly,
    selectAreMaterialInteractionsBlocked,
    selectIsConflictResolutionInProgress,
} from '../../slices/masterSetPlan'
import { selectProject } from '../../slices/project'
import { selectDrawableGroups } from '../2D/handleToggleDrawableGroups'

export const deleteDrawableGroup = createAction<{ drawableGroupId: number; isAllPlans: boolean }>('deleteDrawableGroup')

export interface DeleteOpeningGroupApiResponse {
    groupDeleted: number
}

export interface DeleteOpeningApiResponse {
    deletedOpenings: number[]
    errors: {
        error: string
        opening_id: number
    }[]
}

export function* handleDeleteDrawableGroup({ payload }: ReturnType<typeof deleteDrawableGroup>): SagaIterator<void> {
    try {
        const { drawableGroupId, isAllPlans } = payload

        const manager: PaperManager | null = yield call(managers.get2DManager)

        if (!manager) return

        const [workspaceTool, selectTool]: [Workspace, Select] = yield call(manager.getTools, [
            Workspace.NAME,
            Select.NAME,
        ])

        const project: Project = yield select(selectProject)
        const activeFloor: ActiveFloor = yield select(selectDrawableActiveFloor)

        if (!workspaceTool && !project && !activeFloor) return

        const allDrawableGroups: GeometryGroup[] = yield select(selectDrawableGroups)

        const currentDrawableGroup = allDrawableGroups?.find((drawableGroup) => drawableGroup.id === drawableGroupId)

        if (!currentDrawableGroup) return

        const isResolving: boolean = yield select(selectIsConflictResolutionInProgress)

        if (isResolving) return

        const areMaterialInteractionsBlocked: boolean = yield select(selectAreMaterialInteractionsBlocked)

        if (areMaterialInteractionsBlocked) return

        const areBaseHomeMaterialsReadOnly: boolean = yield select(selectAreBaseHomeMaterialsReadOnly)
        let openingsToDelete = isAllPlans ? currentDrawableGroup.openings : currentDrawableGroup.drawablesPerTab ?? []

        if (areBaseHomeMaterialsReadOnly) {
            const overlayIds: number[] = yield select(selectCurrentOverlayChunkIds)
            const estimatedOverlayIds: number[] = yield select(selectCurrentEstimatedOptionOverlayChunkIds)
            const optionChunkIds = new Set([...overlayIds, ...estimatedOverlayIds])

            openingsToDelete = openingsToDelete.filter((opening) => isMaterialOpeningInOptions(opening, optionChunkIds))
            if (!openingsToDelete.length) return
        }

        // A group spanning protected pages must be kept; delete only its editable openings.
        const canDeleteEntireGroup =
            isAllPlans &&
            (!areBaseHomeMaterialsReadOnly || openingsToDelete.length === currentDrawableGroup.openings.length)

        if (canDeleteEntireGroup) {
            try {
                yield call(deleteOpeningGroupMaterial, project.id, currentDrawableGroup.id)

                // remove drawables and drawable groups
                yield put({
                    type: UPDATE_OPENING_GROUPS_SUCCESS,
                    payload: {
                        openingGroups: {
                            newGroup: {
                                ...currentDrawableGroup,
                                openings: [],
                            },
                            originalGroup: currentDrawableGroup,
                        },
                    },
                })

                // remove from drawablesLocations
                yield all(currentDrawableGroup.openings.map((opening) => put(deleteDrawableLocation([opening.id]))))

                // remove from blueprint
                yield call(workspaceTool.removeItemsWithOpeningGroupId, currentDrawableGroup.id)
            } catch (e) {
                yield call(console.error, `Error on API drawable group in all plans deletion: ${e}`)
            }
        } else {
            const drawablesIdsToDelete = openingsToDelete.map((opening) => opening.id)

            if (!drawablesIdsToDelete?.length) return

            try {
                const res: SagaReturnType<typeof deleteOpenings> = yield call(
                    deleteOpenings,
                    project.id,
                    drawablesIdsToDelete
                )

                if (res.errors?.length > 0) {
                    throw new Error(
                        `Deletion was unsuccessful: ${res.errors
                            .map((e) => `Error: ${e.error}, OpeningId: ${e.opening_id}`)
                            .join('. ')}`
                    )
                }

                const openingToKeep = currentDrawableGroup.openings.filter(
                    (openingToKeep) => !res.deletedOpenings.includes(openingToKeep.id)
                )

                // set as empty because we delete everything from the group
                const drawableGroupWithoutDeletedDrawables: ActiveGeometryGroup = {
                    ...currentDrawableGroup,
                    openings: openingToKeep,
                    drawables: openingToKeep,
                }

                // remove drawables and drawable groups
                yield put({
                    type: UPDATE_OPENING_GROUPS_SUCCESS,
                    payload: {
                        openingGroups: {
                            newGroup: drawableGroupWithoutDeletedDrawables,
                            originalGroup: currentDrawableGroup,
                        },
                    },
                })

                // remove only deleted drawable locations from drawablesLocations
                yield all(res.deletedOpenings.map((drawableId) => put(deleteDrawableLocation([drawableId]))))

                // remove only deleted drawables from blueprint
                yield all(
                    res.deletedOpenings.map((openingId) => call(workspaceTool.removeItemsWithDrawableId, openingId))
                )
            } catch (e) {
                yield call(console.error, `Error on API drawable group on floor deletion: ${e}`)
            }
        }

        yield put(setActiveDrawable(null, 0))

        yield put(setActiveDrawableGroup(null))

        yield call(selectTool.exitSelectMode)

        yield call(workspaceTool.lockItems, false)
    } catch (error) {
        console.error(error)
    }
}

export function* watchForDeleteDrawableGroup() {
    yield takeLatest(deleteDrawableGroup.type, handleDeleteDrawableGroup)
}
